import { FC } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ModalStateDialogComponentProps } from "../../interface";
import { cssLengthToPx } from "@CubeComponents/utils/cssLengthToPx";
import { isNonZero, isNonEmpty } from "@Helpers/numberCondition";

const ModalStateDialogComponent: FC<ModalStateDialogComponentProps> = ({
  footerContent,
  modelContent,
  isVisible,
  width = "fit-content",
  minWidth,
  id,
  headerTitle,
  onCancel,
  modalPadding,
  height,
  disableCrossIcon = false,
  footerBorderTop = false,
  footerBorderColor = "#E8F1FC",
  headerColor = "var(--light-blue-tag)",
  zindex,
}) => {
  const handleCloseButtonClick = () => {
    onCancel?.();
  };

  // Accept number (px), "Npx", or "Nrem" — fall back to 24px if the value can't be parsed.
  const resolvedPaddingPx = cssLengthToPx(modalPadding ?? 24);
  const computedPadding = Number.isFinite(resolvedPaddingPx) ? resolvedPaddingPx : 24;
  const toRem = (px: number) => `${px / 16}rem`;

  return (
    isVisible && (
      <Dialog
        id={id}
        open={true}
        onClose={(_event: object, reason: string) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseButtonClick();
        }}
        maxWidth="xl"
        keepMounted
        disableEnforceFocus={true}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "var(--background-scrim)",
            },
          },
        }}
        sx={{
          zIndex: (theme) => (isNonZero(zindex) ? zindex : `${(theme.zIndex.modal || 2000) + 1} !important`),
          "& .MuiPaper-root": {
            width: width,
            minWidth: minWidth,
            height: isNonEmpty(height) ? height : "auto",
            borderRadius: "0.25rem",
          },
        }}
      >
        {headerTitle && (
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "1.5rem",
              fontWeight: 600,
              lineHeight: "1.875rem",
              paddingInlineStart: toRem(computedPadding),
              paddingInlineEnd: `${toRem(computedPadding - 8)} !important`,
              paddingTop: `${toRem(computedPadding / 2)} !important`,
              paddingBottom: `${toRem(computedPadding / 2)} !important`,
              background: headerColor,
              color: "var(--primary-text)",
              width: "100%",
              gap: "1.5rem",
            }}
            data-testid="modalHeaderTitle"
          >
            {headerTitle}
            {!disableCrossIcon && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseButtonClick();
                }}
                data-testid="modalCloseIcon"
              >
                <CloseIcon sx={{ color: "var(--primary-text)" }} />
              </IconButton>
            )}
          </DialogTitle>
        )}

        <DialogContent
          sx={{
            paddingInlineStart: `${toRem(computedPadding)} !important`,
            paddingInlineEnd: `${toRem(computedPadding)} !important`,
            paddingTop: `${toRem(computedPadding)} !important`,
            padding: 0,
            paddingBottom: toRem(computedPadding / 2),
          }}
          data-testid="modalContent"
        >
          {modelContent}
        </DialogContent>

        {footerContent && (
          <div
            data-testid="modalFooter"
            style={{
              paddingInlineStart: toRem(computedPadding),
              paddingInlineEnd: toRem(computedPadding),
              paddingBottom: toRem(computedPadding / 2 + 4),
              paddingTop: toRem(computedPadding / 2),
              borderTop: footerBorderTop ? `solid 1px ${footerBorderColor}` : 0,
            }}
          >
            {footerContent}
          </div>
        )}
      </Dialog>
    )
  );
};

export default ModalStateDialogComponent;
